import { Helmet } from '@dr.pogodin/react-helmet';
import * as React from 'react';
import { css, html } from 'react-strict-dom';

import { PageHeading, PageWrap, Text } from '../../components';
import { useSelectedCompany } from '../../contexts/ClientDataContext';
import { useMyUserClient } from '../../hooks';

export default function BillingPage() {
  const company = useSelectedCompany();
  const myUser = useMyUserClient();

  const userToCompany = myUser.userToCompanies.find(
    (utc) => utc.company['@id'] === company['@id']
  );

  const rows = [
    <SectionRow key="app-management">App management</SectionRow>,
    <ItemRow
      key="deployment-management"
      label="Deployment management"
      detail={
        'On-going "dev-ops" oversight and management of live system infrastructure'
      }
      value="Yes"
    />,
    <ItemRow
      key="infrastructure-costs"
      label="Infrastructure costs covered"
      detail="All server and other service costs for the system fully covered"
      value="Yes"
    />,
    <ItemRow
      key="performance-uptime"
      label="Performance and uptime monitoring"
      detail="Ensure all components of the system are kept up and running with high performance and availabiltiy"
      value="Yes"
    />,
    <ItemRow
      key="security"
      label="Security"
      detail="Proactive patches and monitoring against potential attack vectors, information security management of customer and other data"
      value="Yes"
    />,
    <ItemRow
      key="error-monitoring"
      label="Error monitoring"
      detail="Tracking errors users encounter in the software (automatically reported), investigating, and applying fixes"
      value="Yes"
    />,
    <ItemRow
      key="platform-updates"
      label="Platform updates"
      detail="Releasing new builds of the apps to meet platform changes (e.g. new iOS releases)"
      value="Yes"
    />,
    <ItemRow
      key="database-management"
      label="Database management"
      detail="Regular backups and distaster recovery"
      value="Yes"
    />,
    <SectionRow key="support">Support</SectionRow>,
    <ItemRow
      key="support-tickets"
      label="Support via tickets"
      detail="Any manner of technical support requests can be entered into the Tickets system, where they will only be closed off once addressed adequately"
      value="Yes"
    />,
    <ItemRow key="support-email" label="Support via email" value="Yes" />,
    <ItemRow
      key="bug-tickets"
      label="Bug tickets covered"
      detail="Technical glitches identified in the system will be addressed without charge"
      value="Yes"
    />,
    <ItemRow
      key="tweak-tickets"
      label="Tweak tickets covered"
      detail="Changes deemed small enough (e.g. copy changes) will be addressed without charge (within reason)"
      value="Yes"
    />,
    <SectionRow key="feature-builds">Feature builds</SectionRow>,
    <ItemRow
      key="priority-scheduling"
      label="Priority scheduling of work"
      value="Yes"
    />,
    <SectionRow key="m10c-console">M10c management console</SectionRow>,
    <ItemRow
      key="business-intelligence"
      label="Business Intelligence"
      detail="Real-time KPI Dashboard and data exports"
      value="Yes"
    />,
  ];

  return (
    <PageWrap limitWidth="small">
      <Helmet>
        <title>Monthly Plan | {company.name}</title>
      </Helmet>

      <PageHeading>Monthly Plan</PageHeading>

      <Text as="p">
        The current pricing and features of your monthly maintenance plan are
        covered below. If you&apos;d like to discuss any of these, please get in
        touch. Please note the following:
      </Text>

      <ul style={{ marginBottom: 40 }}>
        <Text as="li">
          Exact features are subject to change, and the list below only reflects
          services offered at this point in time.
        </Text>
        <Text as="li">
          Specific nuances of your agreement discussed elsewhere may not be
          reflected below.
        </Text>
        <Text as="li">
          Pricing is guided by several factors including: usage levels of your
          apps, size of your team, complexity of your apps. It may need to be
          reviewed from time to time if these factors change, to reflect scaling
          requirements.
        </Text>
        <Text as="li">Quoted prices are excluding VAT.</Text>
      </ul>

      <html.div style={styles.tableWrap}>
        <html.div style={styles.table}>
          {(myUser.agencyRole === 'owner' || userToCompany?.finance) && (
            <html.div style={styles.row}>
              <html.div style={[styles.cell, styles.firstCol]} />
              <Text as="p" style={styles.cell}>
                £{company.retainer} per month
              </Text>
            </html.div>
          )}
          {rows.map((row, i) => (
            <html.div
              key={i}
              style={[styles.row, i % 2 === 0 && styles.rowAlt]}
            >
              {row}
            </html.div>
          ))}
        </html.div>
      </html.div>
    </PageWrap>
  );
}

function SectionRow({ children }: { children: React.ReactNode }) {
  return (
    <Text as="p" style={[styles.cell, styles.sectionHeader, styles.firstCol]}>
      {children}
    </Text>
  );
}

function ItemRow({
  label,
  detail,
  value,
}: {
  label: string;
  detail?: string;
  value: string;
}) {
  return (
    <>
      <html.div style={[styles.cell, styles.bodyHead, styles.firstCol]}>
        <Text>{label}</Text>
        {detail && <Text style={styles.detail}>{detail}</Text>}
      </html.div>
      <Text as="p" style={styles.cell}>
        {value}
      </Text>
    </>
  );
}

const styles = css.create({
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    color: 'rgba(14, 30, 37, 0.87)',
    backgroundColor: 'rgba(14, 30, 37, 0.02)',
    boxShadow:
      '0 0 0 1px rgba(255, 255, 255, 0.1), 0 2px 4px 0 rgba(14, 30, 37, 0.12)',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  rowAlt: {
    backgroundColor: 'rgba(14, 30, 37, 0.02)',
  },
  cell: {
    flexGrow: 1,
    flexShrink: 1,
    width: '25%',
    minWidth: '11.089em',
    paddingTop: 17,
    paddingBottom: 17,
    paddingInline: 20,
    textAlign: 'center',
    fontWeight: '600',
  },
  firstCol: {
    textAlign: 'left',
  },
  sectionHeader: {
    backgroundColor: '#e9e3ed',
    fontWeight: '700',
  },
  bodyHead: {
    fontWeight: '400',
  },
  detail: {
    color: '#888',
    display: 'block',
    fontWeight: '400',
    fontStyle: 'normal',
  },
});
